import type { ApiError } from "../../server/types";

// ────────────────────────────────────────────────────────────────────────
// Thin JSON fetch wrapper used by every React Query hook.
//
// The wrapper takes the burden off caller code in three places:
//   1. Includes cookies so the session cookie set by /api/auth/login
//      travels on every subsequent request.
//   2. Parses JSON and throws a typed `ApiFetchError` on non-2xx
//      responses, carrying the stable `error.code` the server returns.
//      The UI maps codes to localized copy — never surfaces raw text.
//   3. Normalises networking failures (DNS, abort) into the same error
//      type so the UI has one failure branch to render.
// ────────────────────────────────────────────────────────────────────────

export class ApiFetchError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message?: string) {
    super(message ?? code);
    this.name = "ApiFetchError";
    this.status = status;
    this.code = code;
  }
}

interface FetchOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
}

export async function apiFetch<T>(
  path: string,
  options: FetchOptions = {},
): Promise<T> {
  const { method = "GET", body, signal } = options;
  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  const init: RequestInit = {
    method,
    headers,
    credentials: "include",
    signal,
  };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }

  let response: Response;
  try {
    response = await fetch(path, init);
  } catch (err) {
    if ((err as Error).name === "AbortError") {
      throw new ApiFetchError(0, "aborted");
    }
    throw new ApiFetchError(0, "network");
  }

  // 204 No Content — resolve with an empty object cast to T so callers
  // that expect `{ ok: true }` or similar don't have to special-case it.
  if (response.status === 204) {
    return {} as T;
  }

  const text = await response.text();
  const json: unknown = text ? safeParse(text) : null;

  if (!response.ok) {
    const code = extractCode(json) ?? `http_${response.status}`;
    throw new ApiFetchError(response.status, code);
  }
  return (json as T) ?? ({} as T);
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** Pull the stable code out of an ApiError body, if present. */
function extractCode(body: unknown): string | undefined {
  if (
    body &&
    typeof body === "object" &&
    "error" in body &&
    (body as ApiError).error?.code
  ) {
    return (body as ApiError).error.code;
  }
  return undefined;
}
